import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AppShell, PageHeader } from '@/components/AppShell';
import { MapPicker } from '@/components/MapPicker';
import { api } from '@/lib/api';
import { useApp } from '@/lib/store';
import { getCurrentPosition } from '@/lib/geo';
import { cacheRide, queueRide, newClientId, newLocalPublicRideId } from '@/lib/offline';
import { VEHICLE_LABELS } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { MapPin, ArrowRight, ArrowLeft, Check, Bike, Bus, Car, Truck, Navigation, Loader2, Home, Briefcase, Star, Bookmark, X, LocateFixed, BookUser } from 'lucide-react';

const VEHICLES = ['tricycle', 'jeepney', 'taxi', 'ride-share', 'van', 'bus', 'other'];
const VIcon = { tricycle: Bike, jeepney: Bus, taxi: Car, 'ride-share': Car, van: Truck, bus: Bus, other: Navigation };
const PlaceIcon = { home: Home, work: Briefcase, custom: Star };

export default function StartRide() {
  const navigate = useNavigate();
  const t = useT();
  const user = useApp((s) => s.user);
  const setActiveRideId = useApp((s) => s.setActiveRideId);

  const [step, setStep] = useState(0);
  const [destination, setDestination] = useState('');
  const [destPos, setDestPos] = useState(null);       // {lat,lng} chosen on map
  const [origin, setOrigin] = useState(null);          // {latitude,longitude}
  const [geoState, setGeoState] = useState('finding'); // finding | found | none
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState(null);
  const [manualName, setManualName] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [manualPhone, setManualPhone] = useState('');
  const [saveContact, setSaveContact] = useState(true);
  const [nameFocus, setNameFocus] = useState(false);
  const [vehicle, setVehicle] = useState('tricycle');
  const [busy, setBusy] = useState(false);
  const [places, setPlaces] = useState([]);
  const [locatingOrigin, setLocatingOrigin] = useState(false);
  const [savingPlace, setSavingPlace] = useState(false);
  const [namingCustom, setNamingCustom] = useState(false);
  const [customName, setCustomName] = useState('');

  const loadPlaces = useCallback(() => api.listPlaces().then(setPlaces).catch((e) => console.debug('listPlaces failed:', e)), []);

  const relocateOrigin = async () => {
    setLocatingOrigin(true);
    setGeoState('finding');
    try {
      const p = await getCurrentPosition();
      setOrigin(p);
      setGeoState('found');
    } catch (e) {
      console.debug('relocate origin failed:', e);
      setGeoState('none');
    } finally {
      setLocatingOrigin(false);
    }
  };

  // One-time setup: get the current location and load saved places.
  useEffect(() => {
    getCurrentPosition()
      .then((p) => { setOrigin(p); setGeoState('found'); })
      .catch((e) => { console.debug('initial locate failed:', e); setGeoState('none'); });
    loadPlaces();
  }, [loadPlaces]);

  // Load the trusted contact reactively to auth state.
  useEffect(() => {
    if (user) { api.listContacts().then(setContacts).catch((e) => console.debug('listContacts failed:', e)); return; }
    const local = localStorage.getItem('sakaysafe-guest-contact');
    if (local) {
      try { const c = JSON.parse(local); setManualName(c.name || ''); setManualEmail(c.email || ''); setManualPhone(c.phone || ''); }
      catch (err) { console.warn('Bad saved guest contact:', err); }
    }
  }, [user]);

  const pickPlace = (p) => {
    setDestPos({ lat: p.lat, lng: p.lng });
    setDestination(p.address_text || p.label || '');
    toast.success(t('fp_picked').replace('{label}', p.label));
  };

  const saveCurrentAs = async (kind, label = null) => {
    if (!destPos) { toast.error(t('fp_pick_first')); return; }
    setSavingPlace(true);
    try {
      await api.savePlace({ kind, label: label || undefined, lat: destPos.lat, lng: destPos.lng, address_text: destination || null });
      await loadPlaces();
      toast.success(t('fp_saved'));
    } catch { toast.error(t('fp_save_err')); }
    finally { setSavingPlace(false); }
  };

  const hasKind = (k) => places.some((p) => p.kind === k);

  const removePlace = async (p) => {
    try {
      await api.deletePlace(p.id);
      setPlaces((prev) => prev.filter((x) => x.id !== p.id));
      toast.success(t('fp_removed'));
    } catch { toast.error(t('fam_err')); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      let tc = { trusted_contact_id: null, trusted_contact_name: null, trusted_contact_email: null, trusted_contact_phone: null };
      if (user && selectedContact && emailOk(selectedContact.email)) {
        tc = { trusted_contact_id: selectedContact.id, trusted_contact_name: selectedContact.name, trusted_contact_email: selectedContact.email, trusted_contact_phone: selectedContact.phone || null };
      } else if (emailOk(manualEmail)) {
        tc = { trusted_contact_id: null, trusted_contact_name: manualName || selectedContact?.name || null, trusted_contact_email: manualEmail.trim(), trusted_contact_phone: manualPhone.trim() || null };
        // Save this trusted contact so it can be reused on future rides.
        if (saveContact) {
          if (user) {
            // Persist server-side; appears in the quick-pick list next time.
            try {
              await api.createContact({ name: tc.trusted_contact_name || t('ar_contact'), email: tc.trusted_contact_email, phone: tc.trusted_contact_phone, is_primary: contacts.length === 0 });
            } catch (err) { console.debug('save contact failed (non-blocking):', err); }
          } else {
            localStorage.setItem('sakaysafe-guest-contact', JSON.stringify({ name: manualName, email: manualEmail.trim(), phone: manualPhone.trim() }));
          }
        }
      }
      if (!emailOk(tc.trusted_contact_email)) {
        toast.error(t('sr_email_required'));
        setStep(1);
        setBusy(false);
        return;
      }
      const payload = {
        id: newClientId(),
        public_ride_id: newLocalPublicRideId(),
        client_started_at: new Date().toISOString(),
        vehicle_type: vehicle,
        origin_lat: origin?.latitude, origin_lng: origin?.longitude,
        destination_text: destination || null,
        destination_lat: destPos?.lat, destination_lng: destPos?.lng,
        ...tc,
      };
      if (navigator.onLine) {
        try {
          const ride = await api.createRide(payload);
          setActiveRideId(ride.id);
          api.track('first_ride_started', { vehicle_type: vehicle });
          navigate(`/app/ride/${ride.id}`, { replace: true });  // instant start
          return;
        } catch (err) {
          console.warn('online createRide failed, starting offline:', err);
        }
      }
      // Offline (or create failed): start the ride locally now, sync when back online.
      const localRide = {
        ...payload,
        user_id: user?.user_id || null,
        started_at: payload.client_started_at,
        status: 'active',
        has_active_share: false,
        pending_sync: true,
        photos: [],
      };
      await cacheRide(localRide);
      await queueRide(payload);
      setActiveRideId(payload.id);
      api.track('first_ride_started', { vehicle_type: vehicle });
      navigate(`/app/ride/${payload.id}`, { replace: true });
      toast.info(t('sr_started_offline'), { duration: 7000 });
    } catch (e) {
      toast.error(t('sr_err'));
      setBusy(false);
    }
  };

  const next = () => setStep((s) => Math.min(2, s + 1));
  const back = () => (step === 0 ? navigate('/app') : setStep((s) => s - 1));

  // A trusted-contact email is REQUIRED so an SOS always has a recipient.
  const emailOk = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || '').trim());
  const selectedContact = contacts.find((x) => x.id === contactId);
  // Typeahead: signed-in users see matching saved contacts as they type a name.
  const nameSuggestions = (user && manualName.trim().length > 0)
    ? contacts.filter((c) => `${c.name || ''} ${c.email || ''}`.toLowerCase().includes(manualName.trim().toLowerCase())).slice(0, 5)
    : [];
  const pickSuggestion = (c) => {
    setContactId(c.id);
    setManualName(c.name || '');
    setManualEmail(c.email || '');
    setManualPhone(c.phone || '');
    setNameFocus(false);
  };

  // Device Contact Picker (Android Chrome; unsupported on iOS Safari & desktop).
  const contactPickerSupported = typeof navigator !== 'undefined' && 'contacts' in navigator && typeof navigator.contacts?.select === 'function';
  const pickFromPhone = async () => {
    try {
      const selected = await navigator.contacts.select(['name', 'email', 'tel'], { multiple: false });
      if (!selected || !selected.length) return; // user cancelled
      const c = selected[0];
      const first = (v) => (Array.isArray(v) ? v[0] : v) || '';
      setContactId(null);
      const nm = first(c.name); const em = first(c.email); const tel = first(c.tel);
      if (nm) setManualName(nm);
      if (em) setManualEmail(em);
      if (tel) setManualPhone(tel);
      setNameFocus(false);
      toast.success(t('sr_contact_picked'));
    } catch (err) {
      // Permission denied / dismissed — non-fatal.
      console.debug('contact picker cancelled/failed:', err);
    }
  };
  const hasValidContact = (selectedContact && emailOk(selectedContact.email)) || emailOk(manualEmail);
  const nextFromContact = () => {
    if (!hasValidContact) { toast.error(t('sr_email_required')); return; }
    next();
  };

  const locMsg = geoState === 'found' ? t('sr_found') : geoState === 'none' ? t('sr_nolocation') : t('sr_finding');

  return (
    <AppShell nav={false}>
      <button onClick={back} className="inline-flex items-center gap-1 text-sm font-semibold mb-3" style={{ color: 'var(--text-muted)' }}>
        <ArrowLeft size={18} /> {t('back')}
      </button>

      <div className="flex gap-1.5 mb-5">
        {[0, 1, 2].map((i) => (
          <span key={i} className="h-1.5 flex-1 rounded-full" style={{ background: i <= step ? 'var(--primary)' : 'var(--border)' }} />
        ))}
      </div>

      {step === 0 && (
        <div className="ss-fade-in">
          <PageHeader title={t('sr_where')} subtitle={t('sr_where_sub')} />

          <div className="text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }} data-testid="start-ride-from-label">{t('sr_from')}</div>
          <div className="rounded-2xl p-3 border flex items-center gap-2 mb-4" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            <MapPin size={20} style={{ color: geoState === 'found' ? 'var(--success)' : 'var(--text-muted)' }} />
            <span className="flex-1 text-sm" style={{ color: 'var(--text-muted)' }} data-testid="start-ride-geo-status">{locMsg}</span>
            <button
              type="button"
              onClick={relocateOrigin}
              disabled={locatingOrigin}
              aria-label={t('sr_locate')}
              data-testid="start-ride-locate-button"
              className="inline-flex items-center justify-center rounded-xl border min-h-[40px] w-10 shrink-0 disabled:opacity-50"
              style={{ borderColor: 'var(--border)', background: 'var(--surface-alt)', color: 'var(--primary)' }}
            >
              {locatingOrigin ? <Loader2 size={17} className="animate-spin" /> : <LocateFixed size={17} />}
            </button>
          </div>

          <div className="text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }} data-testid="start-ride-to-label">{t('sr_to')}</div>

          {places.length > 0 && (
            <div className="mb-3" data-testid="favourite-places-row">
              <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>{t('fp_quick')}</div>
              <div className="flex flex-wrap gap-2">
                {places.map((p) => {
                  const I = PlaceIcon[p.kind] || Star;
                  return (
                    <div key={p.id} className="inline-flex items-center rounded-full border overflow-hidden" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                      <button onClick={() => pickPlace(p)} data-testid={`fav-place-${p.kind}`} className="inline-flex items-center gap-1.5 pl-3 pr-2 py-2 text-sm font-semibold" style={{ color: 'var(--text)' }}>
                        <I size={15} style={{ color: 'var(--primary)' }} /> {p.label}
                      </button>
                      <button onClick={() => removePlace(p)} aria-label={t('fp_remove')} data-testid={`fav-place-remove-${p.kind}`} className="flex items-center justify-center pr-2.5 pl-1 py-2" style={{ color: 'var(--text-muted)' }}>
                        <X size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <MapPicker
            value={destPos}
            onChange={(p) => setDestPos(p)}
            onLabel={(label) => { if (!destination) setDestination(label); }}
            query={destination}
            onQueryChange={setDestination}
            searchPlaceholder={t('sr_dest_ph')}
            height={260}
            hideLocate
            helpText={t('sr_enter_dest')}
            testid="start-ride-map-picker"
          />

          {destPos && (
            <div className="mt-3" data-testid="save-place-row">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}><Bookmark size={13} className="inline mr-1" style={{ color: 'var(--primary)' }} />{t('fp_save_as')}</span>
                <button onClick={() => { setNamingCustom(false); saveCurrentAs('home'); }} disabled={savingPlace} className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold border disabled:opacity-50" style={{ background: 'var(--surface)', borderColor: hasKind('home') ? 'var(--primary)' : 'var(--border)', color: hasKind('home') ? 'var(--primary)' : 'var(--text)' }} data-testid="save-place-home"><Home size={13} /> {t('fp_home')}</button>
                <button onClick={() => { setNamingCustom(false); saveCurrentAs('work'); }} disabled={savingPlace} className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold border disabled:opacity-50" style={{ background: 'var(--surface)', borderColor: hasKind('work') ? 'var(--primary)' : 'var(--border)', color: hasKind('work') ? 'var(--primary)' : 'var(--text)' }} data-testid="save-place-work"><Briefcase size={13} /> {t('fp_work')}</button>
                <button onClick={() => { setCustomName(destination || ''); setNamingCustom((v) => !v); }} disabled={savingPlace} className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold border disabled:opacity-50" style={{ background: namingCustom ? 'color-mix(in srgb, var(--primary) 12%, transparent)' : 'var(--surface)', borderColor: namingCustom ? 'var(--primary)' : 'var(--border)', color: namingCustom ? 'var(--primary)' : 'var(--text)' }} data-testid="save-place-custom"><Star size={13} /> {t('fp_save_custom')}</button>
              </div>
              {namingCustom && (
                <div className="mt-2 flex items-center gap-2" data-testid="custom-place-namer">
                  <input
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    placeholder={t('fp_name_ph')}
                    maxLength={40}
                    data-testid="custom-place-name-input"
                    className="flex-1 rounded-xl px-3 min-h-[44px] border text-sm outline-none"
                    style={{ background: 'var(--surface)', borderColor: 'var(--border)', color: 'var(--text)' }}
                  />
                  <button
                    onClick={async () => { await saveCurrentAs('custom', customName.trim()); setNamingCustom(false); setCustomName(''); }}
                    disabled={savingPlace || !customName.trim()}
                    data-testid="custom-place-save"
                    className="inline-flex items-center justify-center rounded-xl px-4 min-h-[44px] text-sm font-bold disabled:opacity-50"
                    style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
                  >{t('fp_save_custom')}</button>
                </div>
              )}
            </div>
          )}

          <button data-testid="start-ride-next-0" onClick={next} className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full font-bold min-h-[52px]" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
            {t('next')} <ArrowRight size={20} />
          </button>
        </div>
      )}

      {step === 1 && (
        <div className="ss-fade-in">
          <PageHeader title={t('sr_who')} subtitle={t('sr_who_req_sub')} />
          {user && contacts.length > 0 && (
            <div className="space-y-2 mb-4" data-testid="start-ride-trusted-contact-select">
              {contacts.map((c) => (
                <button key={c.id} onClick={() => setContactId(c.id)} className="w-full text-left rounded-2xl p-3 border flex items-center justify-between" style={{ background: 'var(--surface)', borderColor: contactId === c.id ? 'var(--primary)' : 'var(--border)' }}>
                  <div><div className="font-semibold" style={{ color: 'var(--text)' }}>{c.name}</div><div className="text-xs" style={{ color: emailOk(c.email) ? 'var(--text-muted)' : 'var(--warning)' }}>{c.email || c.phone || t('sr_no_email')}</div></div>
                  {contactId === c.id && <Check size={18} style={{ color: 'var(--primary)' }} />}
                </button>
              ))}
            </div>
          )}
          {(!user || contacts.length === 0 || !(selectedContact && emailOk(selectedContact.email))) && (
            <div className="space-y-3 mb-4">
              {contactPickerSupported && (
                <button
                  type="button"
                  onClick={pickFromPhone}
                  data-testid="start-ride-pick-phone-contact"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl font-semibold min-h-[52px] border text-sm"
                  style={{ background: 'var(--surface)', borderColor: 'var(--primary)', color: 'var(--primary)' }}
                >
                  <BookUser size={18} /> {t('sr_pick_phone')}
                </button>
              )}
              <div className="relative">
                <input value={manualName} onChange={(e) => { setManualName(e.target.value); setContactId(null); }} onFocus={() => setNameFocus(true)} onBlur={() => setTimeout(() => setNameFocus(false), 150)} placeholder={t('sr_name_ph')} autoComplete="off" className="w-full rounded-xl px-4 min-h-[52px] border text-base" style={{ background: 'var(--surface)', borderColor: 'var(--border)', color: 'var(--text)' }} data-testid="start-ride-contact-name" />
                {nameFocus && nameSuggestions.length > 0 && (
                  <div className="absolute z-30 left-0 right-0 mt-1 rounded-xl border overflow-hidden shadow-lg" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="start-ride-name-suggestions">
                    {nameSuggestions.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pickSuggestion(c)}
                        className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:opacity-80"
                        style={{ background: 'transparent' }}
                        data-testid="start-ride-name-suggestion"
                      >
                        <Check size={16} style={{ color: 'var(--primary)', opacity: contactId === c.id ? 1 : 0 }} />
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold truncate" style={{ color: 'var(--text)' }}>{c.name}</span>
                          <span className="block text-xs truncate" style={{ color: 'var(--text-muted)' }}>{c.email || c.phone || t('sr_no_email')}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <input value={manualEmail} onChange={(e) => setManualEmail(e.target.value)} placeholder={t('sr_email_ph')} type="email" inputMode="email" autoComplete="email" className="w-full rounded-xl px-4 min-h-[52px] border text-base" style={{ background: 'var(--surface)', borderColor: manualEmail && !emailOk(manualEmail) ? 'var(--danger)' : 'var(--border)', color: 'var(--text)' }} data-testid="start-ride-contact-email" />
              {manualEmail && !emailOk(manualEmail) && (
                <p className="text-xs" style={{ color: 'var(--danger)' }} data-testid="start-ride-email-error">{t('sr_email_invalid')}</p>
              )}
              <input value={manualPhone} onChange={(e) => setManualPhone(e.target.value)} placeholder={t('sr_phone_ph')} type="tel" inputMode="tel" autoComplete="tel" className="w-full rounded-xl px-4 min-h-[52px] border text-base" style={{ background: 'var(--surface)', borderColor: 'var(--border)', color: 'var(--text)' }} data-testid="start-ride-contact-phone" />
              <button
                type="button"
                onClick={() => setSaveContact((v) => !v)}
                aria-pressed={saveContact}
                data-testid="start-ride-save-contact-toggle"
                className="w-full flex items-center gap-3 rounded-xl px-4 min-h-[52px] border text-left"
                style={{ background: 'var(--surface)', borderColor: saveContact ? 'var(--primary)' : 'var(--border)' }}
              >
                <span
                  className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 border transition-colors"
                  style={{ background: saveContact ? 'var(--primary)' : 'transparent', borderColor: saveContact ? 'var(--primary)' : 'var(--border)', color: 'var(--primary-foreground)' }}
                >
                  {saveContact && <Check size={16} />}
                </span>
                <span className="flex-1">
                  <span className="block text-sm font-semibold" style={{ color: 'var(--text)' }}>{t('sr_save_contact')}</span>
                  <span className="block text-xs" style={{ color: 'var(--text-muted)' }}>{t('sr_save_contact_b')}</span>
                </span>
              </button>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('sr_email_required_note')}</p>
            </div>
          )}
          <button
            data-testid="start-ride-next-1"
            onClick={nextFromContact}
            disabled={!hasValidContact}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full font-bold min-h-[52px] disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
          >
            {t('next')} <ArrowRight size={20} />
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="ss-fade-in">
          <PageHeader title={t('sr_vehicle')} subtitle={t('sr_vehicle_sub')} />
          <div className="grid grid-cols-2 gap-2 mb-5" data-testid="start-ride-vehicle-type-select">
            {VEHICLES.map((v) => {
              const I = VIcon[v];
              const active = vehicle === v;
              return (
                <button key={v} onClick={() => setVehicle(v)} className="rounded-2xl p-4 border flex flex-col items-center gap-2" style={{ background: active ? 'var(--surface-alt)' : 'var(--surface)', borderColor: active ? 'var(--primary)' : 'var(--border)' }} data-testid={`vehicle-${v}`}>
                  <I size={24} style={{ color: active ? 'var(--primary)' : 'var(--text-muted)' }} />
                  <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{VEHICLE_LABELS[v]}</span>
                </button>
              );
            })}
          </div>
          <button
            data-testid="start-ride-confirm-button"
            onClick={confirm}
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full font-bold min-h-[56px] text-base disabled:opacity-60"
            style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
          >
            {busy ? <Loader2 size={20} className="animate-spin" /> : <Check size={20} />} {t('sr_here')}
          </button>
        </div>
      )}
    </AppShell>
  );
}
